import {
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
  createContext,
  useCallback,
} from 'react'
import { isAfter, isBefore, format } from 'date-fns'

import { getRequest } from '../lib/http'

import MaintenanceMessage from '../components/MaintenanceMessage'
import { PageContent } from '../components/LayoutComponents'

import config from '../config'

interface MaintenanceDetails {
  startDateTime: string
  endDateTime: string
  message?: string
}

interface TSystemMaintanceContext {
  checkMaintenanceWindow?: () => Promise<{
    inMaintenanceWindow: boolean
    message?: string
  }>
  updateMaintenanceWindow?: () => Promise<void>
  isInMaintenanceWindow: () => boolean
  startDateTime?: Date
  endDateTime?: Date
  message?: string
}

const SystemMaintanceContext = createContext<TSystemMaintanceContext>({
  isInMaintenanceWindow: () => false,
})

export function useSystemMaintenance() {
  return useContext(SystemMaintanceContext)
}

export default function SystemMaintanceProvider({
  children,
}: PropsWithChildren<Record<string, unknown>>) {
  const [systemMaintenanceState, setSystemMaintenanceState] = useState<{
    startDateTime?: Date
    endDateTime?: Date
    message: string
  }>()

  const fetchMaintenanceWindow = useCallback(
    () =>
      getRequest<MaintenanceDetails>({
        url: `${config.HOSTED_API_URL}/system-maintenance`,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      }),
    [],
  )

  //for checking system maintenance in order to disable app
  const updateMaintenanceWindow = useCallback(async () => {
    try {
      const { startDateTime, endDateTime, message } =
        await fetchMaintenanceWindow()

      if (!startDateTime || !endDateTime) {
        setSystemMaintenanceState({
          message: '',
        })
        return
      }

      const startDate = new Date(startDateTime)
      const endDate = new Date(endDateTime)
      setSystemMaintenanceState({
        startDateTime: startDate,
        endDateTime: endDate,
        message:
          message ||
          `The system is currently unavailable, it will be back online at ${format(
            endDate,
            'h:mm b dd/MM/yyyy',
          )}`,
      })
    } catch (e) {
      setSystemMaintenanceState({
        message: 'The system is currently unavailable, please try again later',
      })
    }
  }, [fetchMaintenanceWindow])

  useEffect(() => {
    updateMaintenanceWindow()
  }, [updateMaintenanceWindow])

  const evaluateDateTimes = useCallback(
    (startDateTime: Date | undefined, endDateTime: Date | undefined) => {
      if (startDateTime && endDateTime) {
        const now = new Date()
        return isAfter(now, startDateTime) && isBefore(now, endDateTime)
      }

      return false
    },
    [],
  )

  //for checking system maintenance without bringing down the app
  const checkMaintenanceWindow = useCallback(async () => {
    try {
      const { startDateTime, endDateTime, message } =
        await fetchMaintenanceWindow()

      if (!startDateTime || !endDateTime) {
        return { inMaintenanceWindow: false }
      }

      const startDate = new Date(startDateTime)
      const endDate = new Date(endDateTime)
      return {
        inMaintenanceWindow: evaluateDateTimes(startDate, endDate),
        message,
      }
    } catch (e) {
      console.error(e)
      return {
        inMaintenanceWindow: true,
        message: 'The system is currently unavailable, please try again later',
      }
    }
  }, [fetchMaintenanceWindow, evaluateDateTimes])

  const isInMaintenanceWindow = useCallback(() => {
    return evaluateDateTimes(
      systemMaintenanceState?.startDateTime,
      systemMaintenanceState?.endDateTime,
    )
  }, [
    evaluateDateTimes,
    systemMaintenanceState?.endDateTime,
    systemMaintenanceState?.startDateTime,
  ])

  if (isInMaintenanceWindow()) {
    return (
      <PageContent>
        <MaintenanceMessage message={systemMaintenanceState?.message ?? ''} />
      </PageContent>
    )
  }

  return (
    <SystemMaintanceContext.Provider
      value={{
        checkMaintenanceWindow,
        updateMaintenanceWindow,
        isInMaintenanceWindow,
        ...systemMaintenanceState,
      }}
    >
      {children}
    </SystemMaintanceContext.Provider>
  )
}
